import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import AdSenseUnit from '../../elements/AdSenseUnit';
import LoadingSpinner from '../../elements/LoadingSpinner';
import useGhostActions from '../../../store/ghost/hooks/useGhostActions';
import { ArticleList, ArticlesContainer, ViewMore, DefaultAdsenseBanner } from './GhostArticles.styles';
import FeaturedArticle from './FeaturedArticle/FeaturedArticle';
import ArticleTile from './ArticleTile';
import ArticleSearch from '../ArticleSearch/ArticleSearch';
/**
 * Handles loading and rendering a single Ghost article. In order for this
 * component to work correctly, you must integrate the `ghost` pantheon redux model
 *
 * This component is NOT themable
 *
 * ## Customization
 *
 * To add custom fonts, you will need to do so through the `fonts` prop. This prop takes in an object in the form of:
 *
 * ```js
 * {
 *  title: '', // article title
 *  body: '' // other article information (author name, description, etc)
 * }.
 *```
 *
 * If not specified, all of the fonts default to Arial.
 *
 * To add custom colors, you will need to do so through the `colors` prop. This prop takes in an object in the form of:
 *
 * ```js
 * {
 *  background: '', // background for all articles
 *  featuredbg: '', // background for just the featured article
 *  featuredblurb: '', // featured article description
 *  title: '', // article title
 *  published: '', // published date text
 *  bodytext: '', // body text
 *  author: '', // author name text
 *  readtime: '', // reading time text
 *  viewmore: '', // "View More Articles" link at bottom
 *  icon: '', // author icon color
 * }
 *```
 *
 * If not specified, all of the colors default to black.
 *
 * You can add any other custom style by adding in a `theme` object to `ThemeProvider`. To add custom styles, you'll want to create a `theme` object that should look like this:
 *
 * ```js
 * const theme = {
 *  ghostFeaturedBlurb: ``, // featured article description
 *  ghostFeaturedImage: ``, // featured image
 *  ghostFeaturedAuthor: ``, // featured author name text
 *  ghostFeaturedPublished: ``, // featured published date text
 *  ghostAuthor: ``, // author name text
 *  ghostPublished: ``, // published date text
 *  ghostReadTime: ``, // reading time text
 *  ghostTitle: ``, // article title
 *  ghostViewMore: `` // "View More Articles" link at bottom
 * }
 *```
 *
 * To use this theme, you'll need to wrap the GhostArticles component in a theme provider where `theme` is passed in to the theme prop. For more information, read more about theming with ThemeProvider [here](https://styled-components.com/docs/advanced#theming).
 *
 * ## AdSense Integration
 *
 * By passing in an object for the `adsense` prop, you are allowing AdSense integration onto your all articles page. By defualt, two AdSense slots are included on each article: one at the top and one after every 6 articles. Both ads are responsive banner ads by default; however, any styling customizations can be added through the `style` or `className` key in your object.
 *
 * In order to be able to properly integrate with AdSense, you must first make sure that you've followed the documentation and instructions found [here](https://internal.minerva.dev/pantheon/?path=/docs/components-elements-adsenseunit--default). In order to properly integrate, you must create 2 different ad slots to include on the article pages. Once all of the AdSense setup is done, you'll want to pass an object similar to the one below to the `adsense` prop:
 *
 * ```
 * {
 *   { AdSenseTop:
 *       slot: '',
 *       className: '',
 *       style: {},
 *       format: '',
 *       client: '',
 *       layout: '',
 *       layoutKey: '',
 *       responsive: ''
 *   },
 *   { AdSenseMiddle:
 *       slot: '',
 *       className: '',
 *       style: {},
 *       format: '',
 *       client: '',
 *       layout: '',
 *       layoutKey: '',
 *       responsive: '',
 *   },
 * }
 *```
 * If you would like to include both ads, be sure to specifically include `AdSenseTop` and `AdSenseMiddle`. If you're looking to only keep one ad, then only include _either_ `AdSenseTop` or `AdSenseMiddle`. For each one that you include, the `slot` key with a unique value is required; however, any of the other keys are not mandatory to include in your object if the generated AdSense code does not ask for them. Please refer to [these docs](https://internal.minerva.dev/pantheon/?path=/docs/components-elements-adsenseunit--default) to understand what each of these keys mean and how they are related to the AdSense code.
 *
 * ## Example Usage
 * ```js
 *
 * import { GhostArticles } from '@minerva-analytics/pantheon';
 *
 * const colors = {
 *  background: 'red',
 *  featuredbg: 'orange',
 *  featuredblurb: 'yellow',
 *  title: 'green',
 *  published: 'blue',
 *  author: 'violet',
 *  readtime: 'indigo',
 *  viewmore: 'magenta',
 * }
 *
 * const fonts = {
 *  title: 'Arial Black',
 *  body: 'Arial',
 * };
 *
 * const ghostApi = new GhostContentAPI({
 *   url: `https://minerva.minervaarticles.com`,
 *   key: `12345`,
 *   version: `v3`,
 * });
 *
 * <GhostArticle
 *   ghostApi={ghostApi}
 *   colors={colors}
 *   fonts={fonts}
 * />
 * ```
 */

var Articles = function Articles(props) {
  var _useGhostActions = useGhostActions(props.ghostApi),
      getArticles = _useGhostActions.getArticles;

  var status = useSelector(function (state) {
    return state.ghost.getArticlesStatus;
  });
  var load = props.getArticles || getArticles;
  var ArticleTileComponent = props.customTile || ArticleTile;
  var FeaturedArticleComponent = props.featureTile || FeaturedArticle;
  var AdSenseTop = props.adsense && props.adsense.AdSenseTop;
  var AdSenseMiddle = props.adsense && props.adsense.AdSenseMiddle;
  useEffect(function () {
    if (!status.loaded) {
      load();
    }
  }, []);
  var allArticles = useSelector(function (state) {
    return Object.keys(state.ghost.byId).map(function (slug) {
      return state.ghost.byId[slug];
    });
  });

  if (status.failed) {
    return /*#__PURE__*/React.createElement("p", null, props.failureText || 'Something went wrong');
  }

  if (!status.loaded) {
    return /*#__PURE__*/React.createElement(LoadingSpinner, null);
  }

  var sortedArticles = allArticles.sort(function (a, b) {
    return new Date(b.published_at) - new Date(a.published_at);
  });
  var articles = sortedArticles.slice(props.featured ? 1 : 0);

  var AdSense = function AdSense(adPosition, element) {
    var AdSenseUnitComponent = adPosition.className ? AdSenseUnit : DefaultAdsenseBanner;
    return /*#__PURE__*/React.createElement(AdSenseUnitComponent, {
      key: element || null,
      middleAd: adPosition === AdSenseMiddle,
      className: adPosition.className,
      style: adPosition.style,
      client: adPosition.client,
      slot: adPosition.slot,
      format: adPosition.format,
      layout: adPosition.layout,
      layoutKey: adPosition.layoutKey,
      responsive: adPosition.responsive
    });
  };

  var articlesWithAdsense = []; // Adding adsense after every 6 articles

  for (var i = 0; i < articles.length; i += 1) {
    var article = articles[i];
    articlesWithAdsense.push( /*#__PURE__*/React.createElement(ArticleTileComponent, {
      key: article.slug,
      ghostApi: props.ghostApi,
      domain: props.domain,
      article: article,
      piwikEvent: props.piwikEvent,
      fonts: props.fonts,
      colors: props.colors,
      hideAuthor: props.hideAuthor
    }));

    if (AdSenseMiddle && i % 6 === 5) {
      articlesWithAdsense.push(AdSense(AdSenseMiddle, i));
    }
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, props.featured ? /*#__PURE__*/React.createElement(FeaturedArticleComponent, {
    key: sortedArticles[0].slug,
    ghostApi: props.ghostApi,
    domain: props.domain,
    article: sortedArticles[0],
    piwikEvent: props.piwikEvent,
    fonts: props.fonts,
    colors: props.colors,
    hideAuthor: props.hideAuthor
  }) : null, AdSenseTop ? AdSense(AdSenseTop) : null, /*#__PURE__*/React.createElement(ArticlesContainer, {
    colors: props.colors
  }, /*#__PURE__*/React.createElement(ArticleSearch, {
    ghostApi: props.ghostApi,
    domain: props.domain
  }), /*#__PURE__*/React.createElement(ArticleList, {
    colors: props.colors
  }, articlesWithAdsense), status.next ? /*#__PURE__*/React.createElement(ViewMore, {
    colors: props.colors,
    fonts: props.fonts,
    onClick: function onClick() {
      load();
    }
  }, "View More Articles") : null, status.loaded && status.loading ? /*#__PURE__*/React.createElement(LoadingSpinner, null) : null));
};

Articles.propTypes = {
  /** The initialized Ghost Content API: https://ghost.org/docs/api/v3/javascript/content/#working-example */
  ghostApi: PropTypes.object.isRequired,

  /** Optional custom event that is logged upon article onclick. Must be an array in the form of `[category, action, name, value]`, though `name` and `value` are optional. */
  piwikEvent: PropTypes.array,

  /** Optional object that contains various colors that can be applied to the articles view */
  colors: PropTypes.object,

  /** Optional object that contains fonts that you can apply to the articles view. */
  fonts: PropTypes.object,

  /** Optional custom component that can be applied to the articles view that replaces the default article tile component. Supports body, title, and author */
  customTile: PropTypes.func,

  /** Optional function that returns the articles object  */
  getArticles: PropTypes.func,

  /** Optional custom failure text if the article fails */
  failureText: PropTypes.string,

  /** Optional custom featured article component */
  featureTile: PropTypes.func,

  /** Optionally set to false if you don't want a featured article in the view */
  featured: PropTypes.bool,

  /** Custom object which includes all of the relevant AdSense information for 2 ads. If an object is passed in for this prop, then this allows for AdSense integration on this page. See documentation above to properly format your object. */
  adsense: PropTypes.object,
  hideAuthor: PropTypes.bool,
  domain: PropTypes.string
};
Articles.defaultProps = {
  featured: true,
  hideAuthor: false
};
export default Articles;